import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { getCategory } from "@/utils/content";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = getCategory(slug);

  const list = articles
    .filter((a) => a.category === slug)
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  const latest = [...articles].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 5);

  const allTags = Array.from(new Set(articles.flatMap((a) => a.tags))).slice(0, 14);

  useSeo({
    title: category
      ? `${category.name}｜灵灵事件信息梳理与解读`
      : "栏目不存在｜灵灵事件信息梳理站",
    description: category
      ? `${category.desc}本站持续更新灵灵黑料相关公开信息，并标注每条内容的可信等级。`
      : "未找到该栏目，请返回首页查看灵灵事件的完整梳理内容。",
    keywords: category
      ? `灵灵${category.name},灵灵黑料,灵灵后续进展,辟谣分析`
      : "灵灵黑料,灵灵事件梳理",
    path: `/category/${slug}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: category ? `${category.name} - 灵灵事件信息梳理` : "栏目不存在",
        description: category?.desc,
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页" },
          { "@type": "ListItem", position: 2, name: category?.name ?? "栏目" },
        ],
      },
    ],
  });

  if (!category) {
    return (
      <SiteLayout>
        <div className="mx-auto max-w-content px-4 py-20 text-center md:px-6">
          <i className="ri-ghost-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这个栏目</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已被合并或改名，回到首页可以查看全部内容。</p>
          <Link
            to="/"
            className="mt-6 inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
          <Breadcrumb items={[{ label: category.name }]} />
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50 md:h-14 md:w-14">
                <i className={`${category.icon} text-2xl`} aria-hidden="true"></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                  {category.name}
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{category.desc}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
              <span className="font-heading text-2xl font-bold text-primary-600">{list.length}</span>
              <span className="text-xs text-foreground-500">篇内容</span>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-8">
            {list.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {list.map((a) => (
                  <ArticleCard key={a.id} article={a} />
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center text-sm text-foreground-500">
                该栏目正在整理中，稍后会补充内容。
              </p>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">切换栏目</h2>
                <ul className="mt-4 space-y-1.5">
                  {categories.map((c) => (
                    <li key={c.slug}>
                      <Link
                        to={`/category/${c.slug}`}
                        className={`flex cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2 text-sm transition-colors ${
                          c.slug === slug
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className={c.icon} aria-hidden="true"></i>
                          {c.name}
                        </span>
                        <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">最新更新</h2>
                <div className="mt-2">
                  {latest.map((a) => (
                    <ArticleCard key={a.id} article={a} variant="compact" />
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-bold text-foreground-950">热门标签</h2>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {allTags.map((t) => (
                    <li
                      key={t}
                      className="rounded-full border border-secondary-200 bg-secondary-50 px-3 py-1 text-xs text-secondary-900"
                    >
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}